import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faAddressBook, faPhone, faEnvelope, faLock } from '@fortawesome/free-solid-svg-icons';
import { Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';

import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { useSuggestedData } from '../../../../../context/SuggestedDataContext';
import { DataSourceBadge, EditButton } from '../../../../Common';
import { Button } from '../../../../ui';
import { SuggestedDataSection } from '../SuggestedDataLoadState';
import { validatePhoneValue, parseToE164 } from '../../../../../utils/phoneUtils';

// Configuración para selección automática de datos de bureaus
const PRIORITIZED_DATA_SOURCES = ['siisa']; // Lista de sources priorizados, ordenados por prioridad
const RENEWER_CLIENT_TYPE = 'RENEWER';

const PHONE_PLACEHOLDER = 'Ej: 11 2345 6789';
const PHONE_HELPER_TEXT = 'Podés ingresar con o sin espacios.';

const ContactInfo = forwardRef(({ 
  formData, 
  handleChange, 
  activeSection, 
  sectionName, 
  sectionValidity, 
  setSectionValidity, 
  handleSectionClick,
  isLoadingSuggestions,
  suggestionLoadFailed = false,
  onRetrySuggestions,
  displayMode = 'accordion',
  clientData
}, ref) => {
  const isContinuousMode = displayMode === 'continuous';
  const { suggestedData, selectedData, setSelectedData, setSuggestedData } = useSuggestedData();
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  // Estados removidos: showNewContactModal, showAllSuggestions ya no son necesarios
  
  // Estados para manejo separado de email y teléfono
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [phoneInputEnabled, setPhoneInputEnabled] = useState(false);
  const [emailInputEnabled, setEmailInputEnabled] = useState(false);
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [phoneWasModified, setPhoneWasModified] = useState(false);

  // Obtener campos de solo lectura desde variable de entorno
  const readonlyFields = process.env.REACT_APP_READONLY_CONTACT_INFO
    ?.split(',')
    .map(f => f.trim())
    .filter(f => f !== '') || [];
  const isPhoneReadonly = readonlyFields.includes('phone');
  const isEmailReadonly = readonlyFields.includes('email');

  const suggestions = suggestedData.contact || [];

  // Helper functions para extraer datos principales de la nueva API
  const getPrimaryPhone = () => {
    // Excluir sugerencias reemplazadas por edición
    const activeSuggestions = suggestions.filter(sugg => !sugg.replacedBy);

    // Buscar teléfono primario (is_primary: true) y activo
    const primaryPhone = activeSuggestions.find(sugg =>
      sugg.contact_type === 'phone' &&
      sugg.is_primary === true &&
      sugg.is_active !== false &&
      !sugg.deactivated_at
    );

    if (primaryPhone) {
      return {
        id: primaryPhone.id,
        value: primaryPhone.mobile || primaryPhone.phone,
        source: primaryPhone.source,
        type_label: primaryPhone.type_label,
        is_verified: primaryPhone.is_verified,
        used_in_requests: primaryPhone.used_in_requests
      };
    }

    // Si no hay primario, buscar el primer teléfono activo
    const firstPhone = activeSuggestions.find(sugg =>
      sugg.contact_type === 'phone' &&
      sugg.is_active !== false &&
      !sugg.deactivated_at
    );
    
    if (firstPhone) {
      return {
        id: firstPhone.id,
        value: firstPhone.mobile || firstPhone.phone,
        source: firstPhone.source,
        type_label: firstPhone.type_label,
        is_verified: firstPhone.is_verified,
        used_in_requests: firstPhone.used_in_requests
      };
    }
    
    return null;
  };

  const getPrimaryEmail = () => {
    // Excluir sugerencias reemplazadas por edición
    const activeSuggestions = suggestions.filter(sugg => !sugg.replacedBy);

    // Buscar email primario (is_primary: true) y activo
    const primaryEmail = activeSuggestions.find(sugg =>
      sugg.contact_type === 'email' &&
      sugg.is_primary === true &&
      sugg.is_active !== false &&
      !sugg.deactivated_at
    );

    if (primaryEmail) {
      return {
        id: primaryEmail.id,
        value: primaryEmail.email,
        source: primaryEmail.source,
        is_verified: primaryEmail.is_verified,
        is_valid: primaryEmail.is_valid,
        used_in_requests: primaryEmail.used_in_requests
      };
    }

    // Si no hay primario, buscar el primer email activo
    const firstEmail = activeSuggestions.find(sugg =>
      sugg.contact_type === 'email' &&
      sugg.is_active !== false &&
      !sugg.deactivated_at
    );
    
    if (firstEmail) {
      return {
        id: firstEmail.id,
        value: firstEmail.email,
        source: firstEmail.source,
        is_verified: firstEmail.is_verified,
        is_valid: firstEmail.is_valid,
        used_in_requests: firstEmail.used_in_requests
      };
    }
    
    return null;
  };


  // Componente simplificado para mostrar datos principales
  const renderSimplifiedContactData = () => {
    const primaryPhone = getPrimaryPhone();
    const primaryEmail = getPrimaryEmail();
    
    // Determinar si un campo es efectivamente readonly (solo si tiene valor)
    const isPhoneEffectivelyReadonly = isPhoneReadonly && primaryPhone?.value;
    const isEmailEffectivelyReadonly = isEmailReadonly && primaryEmail?.value;
    
    // Si no hay datos, mostrar formulario de entrada manual
    if (!primaryPhone && !primaryEmail) {
      return renderManualEntryForm();
    }

    return (
      <>
        <div className="row g-3 contact-data-simplified">
          {/* Teléfono Principal */}
          <div className="col-lg-6 col-12">
            <div className="h-100">
              <label className="form-label">
                Teléfono<sup>*</sup>
                {isPhoneEffectivelyReadonly && (
                  <span className="ms-2 text-muted" title="Campo bloqueado por configuración">
                    <FontAwesomeIcon icon={faLock} size="sm" />
                  </span>
                )}
              </label>
              {primaryPhone ? (
                <div className="position-relative">
                  <div className="input-group">
                    <span className="input-group-text">
                      <FontAwesomeIcon icon={faPhone} />
                    </span>
                    <input
                      type="tel"
                      className={`form-control ${errors.phone && touchedFields.phone ? 'is-invalid' : ''}`}
                      value={isEditingPhone ? (formData.phone || '') : primaryPhone.value}
                      onChange={handlePhoneChange}
                      onBlur={(e) => handlePhoneBlur(e, primaryPhone)}
                      name="phone"
                      placeholder={isEditingPhone && !isPhoneEffectivelyReadonly ? PHONE_PLACEHOLDER : undefined}
                      readOnly={!isEditingPhone || isPhoneEffectivelyReadonly}
                      style={{ 
                        backgroundColor: (!isEditingPhone || isPhoneEffectivelyReadonly) ? '#f8f9fa' : 'white',
                        paddingRight: '45px'
                      }}
                    />
                    {!isPhoneEffectivelyReadonly && (
                      <button
                        className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                        onClick={() => handleEditPhone(primaryPhone)}
                        type="button"
                        style={{ 
                          zIndex: 10, 
                          marginRight: '8px',
                          padding: '4px 8px',
                          border: 'none',
                          background: 'transparent'
                        }}
                      >
                        <i className="bi bi-pencil text-muted"></i>
                      </button>
                    )}
                  </div>
                  {isEditingPhone && !isPhoneEffectivelyReadonly && (
                    <div className="mt-2">
                      <Button
                        variant="success"
                        size="sm"
                        className="me-2"
                        onClick={handleSavePhone}
                        type="button"
                      >
                        Guardar
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleCancelEditPhone}
                        type="button"
                      >
                        Cancelar
                      </Button>
                    </div>
                  )}
                  {errors.phone && touchedFields.phone && (
                    <div className="invalid-feedback d-block">{errors.phone}</div>
                  )}
                  {!errors.phone && isEditingPhone && !isPhoneEffectivelyReadonly && (
                    <small className="form-text text-muted d-block mt-1">{PHONE_HELPER_TEXT}</small>
                  )}
                  <DataSourceBadge dataItem={primaryPhone} options={{ position: 'top-right' }} />
                </div>
              ) : (
                <>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FontAwesomeIcon icon={faPhone} />
                    </span>
                    <input
                      type="tel"
                      className={`form-control ${errors.phone && touchedFields.phone ? 'is-invalid' : ''}`}
                      value={formData.phone || ''}
                      onChange={handlePhoneChange}
                      onBlur={(e) => handleFieldBlur(e)}
                      name="phone"
                      placeholder={PHONE_PLACEHOLDER}
                    />
                  </div>
                  {errors.phone && touchedFields.phone && (
                    <div className="invalid-feedback d-block">{errors.phone}</div>
                  )}
                  {!errors.phone && (
                    <small className="form-text text-muted d-block mt-1">{PHONE_HELPER_TEXT}</small>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Email Principal */}
          <div className="col-lg-6 col-12">
            <div className="h-100">
              <label className="form-label">
                Email<sup>*</sup>
                {isEmailEffectivelyReadonly && (
                  <span className="ms-2 text-muted" title="Campo bloqueado por configuración">
                    <FontAwesomeIcon icon={faLock} size="sm" />
                  </span>
                )}
              </label>
              {primaryEmail ? (
                <div className="position-relative">
                  <div className="input-group">
                    <span className="input-group-text">
                      <FontAwesomeIcon icon={faEnvelope} />
                    </span>
                    <input
                      type="email"
                      className={`form-control ${errors.email && touchedFields.email ? 'is-invalid' : ''}`}
                      value={isEditingEmail ? (formData.email || '') : primaryEmail.value}
                      onChange={handleFieldChange}
                      onBlur={(e) => handleEmailBlur(e, primaryEmail)}
                      name="email"
                      readOnly={!isEditingEmail || isEmailEffectivelyReadonly}
                      style={{ 
                        backgroundColor: (!isEditingEmail || isEmailEffectivelyReadonly) ? '#f8f9fa' : 'white',
                        paddingRight: '45px'
                      }}
                    />
                    {!isEmailEffectivelyReadonly && (
                      <button
                        className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                        onClick={() => handleEditEmail(primaryEmail)}
                        type="button"
                        style={{ 
                          zIndex: 10, 
                          marginRight: '8px',
                          padding: '4px 8px',
                          border: 'none',
                          background: 'transparent'
                        }}
                      >
                        <i className="bi bi-pencil text-muted"></i>
                      </button>
                    )}
                  </div>
                  {isEditingEmail && !isEmailEffectivelyReadonly && (
                    <div className="mt-2">
                      <Button
                        variant="success"
                        size="sm"
                        className="me-2"
                        onClick={handleSaveEmail}
                        type="button"
                      >
                        Guardar
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleCancelEditEmail}
                        type="button"
                      >
                        Cancelar
                      </Button>
                    </div>
                  )}
                  {errors.email && touchedFields.email && (
                    <div className="invalid-feedback d-block">{errors.email}</div>
                  )}
                  <DataSourceBadge dataItem={primaryEmail} options={{ position: 'top-right' }} />
                </div>
              ) : (
                <div className="input-group">
                  <span className="input-group-text">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </span>
                  <input
                    type="email"
                    className={`form-control ${errors.email && touchedFields.email ? 'is-invalid' : ''}`}
                    value={formData.email || ''}
                    onChange={handleFieldChange}
                    onBlur={(e) => handleFieldBlur(e)}
                    name="email"
                    placeholder="Ingrese su correo electrónico"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {phoneWasModified && process.env.REACT_APP_VALIDATE_PHONE === 'true' && (
          <div className="row g-0">
            <div className="col-12 g-0">
              <div className="card mb-0">
                <div className="card-body text-muted">
                  <small>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                    El teléfono deberá ser verificado por el cliente más adelante.
                  </small>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Formulario manual cuando no hay datos (comportamiento actual)
  const renderManualEntryForm = () => (
    <div className="contact-data">
      <div className="row g-3">
        {/* Card de Teléfono */}
        <div className="col-lg-6 col-12">
          <div className="h-100">
            <label className="form-label">Teléfono<sup>*</sup></label>
            <div className="input-group">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faPhone} />
              </span>
              <input
                type="tel"
                className={`form-control ${errors.phone && touchedFields.phone ? 'is-invalid' : ''}`}
                value={formData.phone || ''}
                onChange={handlePhoneChange}
                onBlur={(e) => handleFieldBlur(e)}
                name="phone"
                placeholder={PHONE_PLACEHOLDER}
              />
            </div>
            {errors.phone && touchedFields.phone && (
              <div className="invalid-feedback d-block">{errors.phone}</div>
            )}
            {!errors.phone && (
              <small className="form-text text-muted d-block mt-1">{PHONE_HELPER_TEXT}</small>
            )}
          </div>
        </div>

        {/* Card de Email */}
        <div className="col-lg-6 col-12">
          <div className="h-100">
            <label className="form-label">Email<sup>*</sup></label>
            <div className="input-group">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faEnvelope} />
              </span>
              <input
                type="email"
                className={`form-control ${errors.email && touchedFields.email ? 'is-invalid' : ''}`}
                value={formData.email || ''}
                onChange={handleFieldChange}
                onBlur={(e) => handleFieldBlur(e)}
                name="email"
                placeholder="Ingrese su correo electrónico"
              />
            </div>
            {errors.email && touchedFields.email && (
              <div className="invalid-feedback d-block">{errors.email}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Función para auto-seleccionar datos del bureau priorizado para clientes renovadores
  const autoSelectPrioritizedData = () => {
    // Solo aplicar para clientes renovadores
    if (clientData?.client_type?.code !== RENEWER_CLIENT_TYPE) {
      return;
    }

    // Solo aplicar si no hay nada seleccionado aún
    if (selectedData.contact) {
      return;
    }

    // Buscar el dato más reciente entre todos los sources priorizados
    const prioritizedSuggestions = suggestions
      .filter(suggestion => {
        // Verificar si el dato viene de algún source priorizado
        return PRIORITIZED_DATA_SOURCES.some(source => 
          suggestion.source?.toLowerCase() === source.toLowerCase()
        );
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const mostRecentPrioritized = prioritizedSuggestions[0];
    
    if (mostRecentPrioritized) {
      handleUseExisting(mostRecentPrioritized);
    }
  };

  const requiredFields = ['phone', 'email'];

  useEffect(() => {
    const allFieldsTouched = requiredFields.every(field => touchedFields[field]);
    if (allFieldsTouched) {
      validateSection();
    }
    // eslint-disable-next-line
  }, [touchedFields]);

  // UseEffect para auto-seleccionar datos del bureau priorizado y validar automáticamente
  useEffect(() => {
    if (suggestions.length > 0) {
      // Auto-seleccionar para clientes renovadores
      if (clientData) {
        autoSelectPrioritizedData();
      }
      
      // Sincronizar formData con datos principales y validar
      const primaryPhone = getPrimaryPhone();
      const primaryEmail = getPrimaryEmail();
      
      // Actualizar formData automáticamente si hay datos principales y no están en formData
      if (primaryPhone && primaryPhone.value) {
        const canonicalPhone = parseToE164(primaryPhone.value) || primaryPhone.value;
        if (!formData.phone || formData.phone !== canonicalPhone) {
          handleChange({ target: { name: 'phone', value: canonicalPhone } });
        }
        // Si el teléfono actual coincide con el principal, resetear el estado de modificación
        if (formData.phone === canonicalPhone) {
          setPhoneWasModified(false);
        }
      }
      if (primaryEmail && primaryEmail.value && (!formData.email || formData.email !== primaryEmail.value)) {
        handleChange({ target: { name: 'email', value: primaryEmail.value } });
      }
      
      // Validar la sección después de sincronizar
      setTimeout(() => {
        validateSection();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions, clientData]);

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'phone': {
        const result = validatePhoneValue(value);
        error = result.error || '';
        break;
      }
      case 'email':
        if (!value) {
          error = 'El email es requerido';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Por favor, ingresa un email válido';
        }
        break;
      default:
        break;
    }
    setErrors(prev => ({ ...prev, [name]: error }));
    return !error;
  };

  const validateSection = () => {
    const primaryPhone = getPrimaryPhone();
    const primaryEmail = getPrimaryEmail();
    
    // Determinar los valores a validar (usar primaryPhone/primaryEmail si existen, sino formData)
    const phoneValue = primaryPhone?.value || formData.phone;
    const emailValue = primaryEmail?.value || formData.email;
    
    // Asegurar que formData esté sincronizado con los valores principales
    if (primaryPhone && !formData.phone) {
      handleChange({ target: { name: 'phone', value: primaryPhone.value } });
    }
    if (primaryEmail && !formData.email) {
      handleChange({ target: { name: 'email', value: primaryEmail.value } });
    }
    
    // Validar ambos campos
    const hasValidPhone = phoneValue && validateField('phone', phoneValue);
    const hasValidEmail = emailValue && validateField('email', emailValue);
    
    const isValid = hasValidPhone && hasValidEmail;
    setSectionValidity((prev) => ({ ...prev, [sectionName]: isValid }));
    
    // Actualizar selectedData.contact si ambos campos están válidos
    if (isValid) {
      updateContactSelection();
    }
    
    return isValid;
  };

  useImperativeHandle(ref, () => ({
    validateSection
  }));

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    handleChange(e);
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    handleChange({ target: { name, value } });
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    let valueToValidate = value;
    if (name === 'phone' && value) {
      const e164 = parseToE164(value);
      if (e164) {
        handleChange({ target: { name: 'phone', value: e164 } });
        valueToValidate = e164;
      }
    }
    validateField(name, valueToValidate);
    
    // Validar la sección completa después de validar el campo individual
    // Esto asegura que cuando se completa el email manualmente y hay un teléfono readonly,
    // la sección se valide correctamente
    setTimeout(() => {
      validateSection();
    }, 100);
  };

  // Función específica para onBlur de teléfonos con guardado automático
  const handlePhoneBlur = (e, phone) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));

    let valueToValidate = value;
    if (value) {
      const e164 = parseToE164(value);
      if (e164) {
        handleChange({ target: { name: 'phone', value: e164 } });
        valueToValidate = e164;
      }
    }
    
    // Validar el campo
    const isValid = validateField(name, valueToValidate);
    
    // Si no hay teléfono seleccionado pero el input está habilitado, crear un teléfono temporal
    if (phoneInputEnabled && !selectedPhone && value && isValid) {
      const tempPhone = {
        id: `temp_phone_${Date.now()}`,
        value: value,
        source: 'manual',
        created_at: new Date().toISOString()
      };
      setSelectedPhone(tempPhone);
    }
    
    // Solo guardar automáticamente si el campo está habilitado para edición y hay cambios
    if (phone && phoneInputEnabled && selectedPhone?.id === phone.id && isValid) {
      const originalCanonical = parseToE164(phone.value) || phone.value || '';
      const newCanonical = valueToValidate || '';
      if (originalCanonical !== newCanonical) {
        setPhoneWasModified(true);
        handleSavePhone();
      }
    }
    
    // Si el teléfono está en readonly, asegurar que formData esté sincronizado
    if (phone && !isEditingPhone) {
      if (formData.phone !== phone.value) {
        handleChange({ target: { name: 'phone', value: phone.value } });
      }
    }
    
    // Validar la sección después de procesar el campo
    setTimeout(() => validateSection(), 100);
  };

  // Función específica para onBlur de emails con guardado automático
  const handleEmailBlur = (e, email) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    
    // Validar el campo
    const isValid = validateField(name, value);
    
    // Si no hay email seleccionado pero el input está habilitado, crear un email temporal
    if (emailInputEnabled && !selectedEmail && value && isValid) {
      const tempEmail = {
        id: `temp_email_${Date.now()}`,
        value: value,
        source: 'manual',
        created_at: new Date().toISOString()
      };
      setSelectedEmail(tempEmail);
    }
    
    // Solo guardar automáticamente si el campo está habilitado para edición y hay cambios
    if (email && emailInputEnabled && selectedEmail?.id === email.id && isValid) {
      // Verificar si realmente hay cambios
      if (value !== email.value) {
        handleSaveEmail();
      }
    }
    
    // Si el email está en readonly, asegurar que formData esté sincronizado
    if (email && !isEditingEmail) {
      if (formData.email !== email.value) {
        handleChange({ target: { name: 'email', value: email.value } });
      }
    }
    
    // Validar la sección después de procesar el campo
    setTimeout(() => validateSection(), 100);
  };

  const handleUseExisting = (suggestion) => {
    setSelectedData(prev => ({
      ...prev,
      contact: suggestion.id
    }));
    const canonicalPhone = suggestion.mobile ? (parseToE164(suggestion.mobile) || suggestion.mobile) : suggestion.mobile;
    handleChange({ target: { name: 'phone', value: canonicalPhone } });
    handleChange({ target: { name: 'email', value: suggestion.email } });
    setTouchedFields({
      phone: true,
      email: true,
    });
    setSectionValidity(prev => ({ ...prev, [sectionName]: true }));
  };

  // Nueva función para editar teléfono principal
  const handleEditPhone = (phone) => {
    if (!phone) return;
    setSelectedPhone(phone);
    setIsEditingPhone(true);
    handleChange({ target: { name: 'phone', value: phone.value } });
    setTouchedFields(prev => ({ ...prev, phone: true }));
  };

  const handleCancelEditPhone = () => {
    const primaryPhone = getPrimaryPhone();
    setIsEditingPhone(false);
    if (primaryPhone) {
      handleChange({ target: { name: 'phone', value: primaryPhone.value } });
      // Si se cancela y se restaura el valor original, resetear el estado de modificación
      const currentValue = formData.phone || '';
      if (currentValue === primaryPhone.value) {
        setPhoneWasModified(false);
      }
    }
    setErrors(prev => ({ ...prev, phone: '' }));
  };

  // Funciones para manejar teléfonos (comportamiento original para compatibilidad)
  const handleSelectPhone = (phone) => {
    setSelectedPhone(phone);
    setPhoneInputEnabled(true); // Habilitar directamente para edición
    setIsEditingPhone(true); // Marcar como en edición
    handleChange({ target: { name: 'phone', value: phone.value } });
    setTouchedFields(prev => ({ ...prev, phone: true }));
    // No llamar updateContactSelection aquí, se llamará al guardar
  };

  // Función removida: handleEditPhone ya no es necesaria

  const handleSavePhone = () => {
    if (!validateField('phone', formData.phone)) return;

    // Detectar si el teléfono fue modificado (comparar con el valor original)
    const originalPhoneValue = selectedPhone?.value || '';
    const newPhoneValue = formData.phone || '';
    const wasModified = isEditingPhone && selectedPhone && originalPhoneValue !== newPhoneValue;
    
    if (wasModified) {
      setPhoneWasModified(true);
    }

    const phoneData = {
      id: isEditingPhone && selectedPhone ? `edited_${selectedPhone.id}_${Date.now()}` : `new_${Date.now()}`,
      contact_type: 'phone',
      mobile: formData.phone,
      phone: formData.phone,
      email: selectedEmail?.value || formData.email || '',
      created_at: new Date().toISOString(),
      isEdited: isEditingPhone,
      is_primary: true,
      is_active: true,
      originalId: isEditingPhone && selectedPhone ? selectedPhone.id : null
    };

    // Marcar el teléfono original como reemplazado si estamos editando
    let updatedSuggestions;
    if (isEditingPhone && selectedPhone) {
      updatedSuggestions = suggestions.map(suggestion => 
        suggestion.id === selectedPhone.id 
          ? { ...suggestion, replacedBy: phoneData.id }
          : suggestion
      );
      updatedSuggestions = [phoneData, ...updatedSuggestions];
    } else {
      updatedSuggestions = [phoneData, ...suggestions];
    }

    setSuggestedData(prev => ({
      ...prev,
      contact: updatedSuggestions
    }));

    setSelectedPhone({ ...phoneData, value: phoneData.mobile });
    setIsEditingPhone(false);
    setPhoneInputEnabled(false);
    
    // Actualizar selección y validar sección
    updateContactSelection();
    validateSection();

    Swal.fire({
      icon: 'success',
      title: isEditingPhone ? 'Teléfono editado' : 'Teléfono agregado',
      text: isEditingPhone ? 'El teléfono ha sido editado correctamente.' : 'El teléfono ha sido agregado correctamente.',
      timer: 2000,
      showConfirmButton: false
    });
  };

  // Nueva función para editar email principal
  const handleEditEmail = (email) => {
    if (!email) return;
    setSelectedEmail(email);
    setIsEditingEmail(true);
    handleChange({ target: { name: 'email', value: email.value } });
    setTouchedFields(prev => ({ ...prev, email: true }));
  };

  const handleCancelEditEmail = () => {
    const primaryEmail = getPrimaryEmail();
    setIsEditingEmail(false);
    if (primaryEmail) {
      handleChange({ target: { name: 'email', value: primaryEmail.value } });
    }
    setErrors(prev => ({ ...prev, email: '' }));
  };

  // Funciones para manejar emails (comportamiento original para compatibilidad)
  const handleSelectEmail = (email) => {
    setSelectedEmail(email);
    setEmailInputEnabled(true); // Habilitar directamente para edición
    setIsEditingEmail(true); // Marcar como en edición
    handleChange({ target: { name: 'email', value: email.value } });
    setTouchedFields(prev => ({ ...prev, email: true }));
    // No llamar updateContactSelection aquí, se llamará al guardar
  };

  // Función removida: handleEditEmail ya no es necesaria

  const handleSaveEmail = () => {
    if (!validateField('email', formData.email)) return;

    const emailData = {
      id: isEditingEmail && selectedEmail ? `edited_${selectedEmail.id}_${Date.now()}` : `new_${Date.now()}`,
      contact_type: 'email',
      mobile: selectedPhone?.value || formData.phone || '',
      email: formData.email,
      created_at: new Date().toISOString(),
      isEdited: isEditingEmail,
      is_primary: true,
      is_active: true,
      originalId: isEditingEmail && selectedEmail ? selectedEmail.id : null
    };

    // Marcar el email original como reemplazado si estamos editando
    let updatedSuggestions;
    if (isEditingEmail && selectedEmail) {
      updatedSuggestions = suggestions.map(suggestion => 
        suggestion.id === selectedEmail.id 
          ? { ...suggestion, replacedBy: emailData.id }
          : suggestion
      );
      updatedSuggestions = [emailData, ...updatedSuggestions];
    } else {
      updatedSuggestions = [emailData, ...suggestions];
    }

    setSuggestedData(prev => ({
      ...prev,
      contact: updatedSuggestions
    }));

    setSelectedEmail({ ...emailData, value: emailData.email });
    setIsEditingEmail(false);
    setEmailInputEnabled(false);
    
    // Actualizar selección y validar sección
    updateContactSelection();
    validateSection();

    Swal.fire({
      icon: 'success',
      title: isEditingEmail ? 'Email editado' : 'Email agregado',
      text: isEditingEmail ? 'El email ha sido editado correctamente.' : 'El email ha sido agregado correctamente.',
      timer: 2000,
      showConfirmButton: false
    });
  };

  // Función para actualizar la selección de contacto en el contexto global
  const updateContactSelection = () => {
    // Si hay datos válidos en formData, actualizar la selección
    if (formData.phone && formData.email) {
      // Crear IDs temporales si no existen selectedPhone/selectedEmail
      const phoneId = selectedPhone?.id || `temp_phone_${Date.now()}`;
      const emailId = selectedEmail?.id || `temp_email_${Date.now()}`;
      
      const contactId = `contact_${phoneId}_${emailId}`;
      setSelectedData(prev => ({
        ...prev,
        contact: contactId
      }));
    } else if (formData.phone || formData.email) {
      // Si solo uno está completado
      const contactId = selectedPhone?.id || selectedEmail?.id || `temp_contact_${Date.now()}`;
      setSelectedData(prev => ({
        ...prev,
        contact: contactId
      }));
    }
  };

  // Funciones y variables removidas: modal ya no es necesario

  // Renderizado condicional basado en el modo de visualización
  if (isContinuousMode) {
    return (
      <div className="continuous-section mb-2">
        <div className="section-header mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="d-flex align-items-center mb-0 fw-light rg-sale-section__title">
              <FontAwesomeIcon icon={faAddressBook} className="me-2" />
              Datos de Contacto
              {sectionValidity[sectionName] === true && (
                <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
              )}
              {sectionValidity[sectionName] === false && (
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
              )}
            </h5>
          </div>
        </div>
        <div className="section-content">
          <SuggestedDataSection
            loading={isLoadingSuggestions}
            failed={suggestionLoadFailed}
            loadingLabel="Cargando contactos sugeridos..."
            onRetry={onRetrySuggestions}
          >
            {renderSimplifiedContactData()}
          </SuggestedDataSection>
        </div>

        {/* Modal removido: ya no es necesario */}
      </div>
    );
  }

  // Modo accordion (original)
  return (
    <div className="accordion-item mb-1">
      <h2 className="accordion-header">
        <button
          className={`accordion-button ${activeSection === sectionName ? '' : 'collapsed'}`}
          type="button"
          onClick={() => handleSectionClick(sectionName)}
        >
          <FontAwesomeIcon icon={faAddressBook} className="me-2" />
          Datos de Contacto
          {sectionValidity[sectionName] === true && (
            <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
          )}
          {sectionValidity[sectionName] === false && (
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
          )}
        </button>
      </h2>
      <div className={`accordion-collapse collapse ${activeSection === sectionName ? 'show' : ''}`}>
        <div className="accordion-body">
          <SuggestedDataSection
            loading={isLoadingSuggestions}
            failed={suggestionLoadFailed}
            loadingLabel="Cargando contactos sugeridos..."
            onRetry={onRetrySuggestions}
          >
            {renderSimplifiedContactData()}
          </SuggestedDataSection>
        </div>
      </div>

      {/* Modal removido: ya no es necesario */}
    </div>
  );
});

export default ContactInfo; 